// src/components/dashboard/StreakCard.js
import React, { useState, useEffect } from 'react';
import { SHIELD_CAP, SHIELD_EARN_DAYS } from '../../utils/streak';
import { displayListens } from '../../utils/listeningStats';

// Custom hook to check for screen size
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);
  return matches;
}

// Shows the 盾 the user currently holds, as kanji: blue = held, dim = an empty slot.
// 盾 are earned by consistent study and auto-spent to protect the streak on a missed day.
const ShieldsIndicator = ({ shields = 0, className = '' }) => {
  const held = Math.max(0, Math.min(shields, SHIELD_CAP));
  return (
    <div
      className={`inline-flex items-center gap-1 ${className}`}
      title={`You hold ${held} of ${SHIELD_CAP} 盾. Study ${SHIELD_EARN_DAYS} days in a row to earn one — it's auto-spent to protect your streak if you miss a day.`}
    >
      {Array.from({ length: SHIELD_CAP }, (_, i) => (
        <span
          key={i}
          lang="ja"
          className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold leading-none ${
            i < held
              ? 'bg-sky-500 text-white shadow-sm'
              : 'border-2 border-dashed border-border text-content-secondary/30'
          }`}
        >
          盾
        </span>
      ))}
    </div>
  );
};

// Small pill summarizing how many missed days a 盾 protected within the current streak
const ProtectedDaysBadge = ({ protectedDaysUsed, className = '' }) => {
  if (!protectedDaysUsed) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2.5 py-1 text-xs font-medium text-sky-600 dark:text-sky-300 ${className}`}
      title="A missed day was automatically protected by a 盾, so your streak kept going."
    >
      <span lang="ja" className="font-bold leading-none">盾</span>
      {protectedDaysUsed} {protectedDaysUsed === 1 ? 'day' : 'days'} protected
    </span>
  );
};

// Sentences/Words global "avg plays before answering" ratio — shown under the streak
// (mirrors the sentences/words split under Overall Accuracy on the left).
const RatioSplit = ({ sentencesRatio, wordsRatio, className = '' }) => (
  <div className={`border-t border-border pt-3 ${className}`}>
    <div className="flex justify-around gap-2">
      <div className="flex flex-1 flex-col items-center">
        <span className="text-lg font-bold text-secondary">{sentencesRatio != null ? displayListens(sentencesRatio).toFixed(1) : '—'}</span>
        <span className="mt-0.5 text-center text-xs text-content-secondary">plays per sentence review</span>
      </div>
      <div className="flex flex-1 flex-col items-center">
        <span className="text-lg font-bold text-secondary">{wordsRatio != null ? displayListens(wordsRatio).toFixed(1) : '—'}</span>
        <span className="mt-0.5 text-center text-xs text-content-secondary">plays per word review</span>
      </div>
    </div>
  </div>
);

// Full-detail card for Desktop
const DesktopStreakView = ({ currentStreak, protectedDaysUsed, shields, status, sentencesRatio, wordsRatio }) => (
  <div className="relative h-full flex flex-col p-6 rounded-2xl border-2 border-border backdrop-blur-sm transition-all duration-300 overflow-hidden bg-background-secondary">
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50 dark:from-white/5" />
    <div className="absolute inset-0 opacity-5 dark:opacity-10">
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary rounded-full -translate-y-16 translate-x-16" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary rounded-full translate-y-12 -translate-x-12" />
    </div>
    <div className="absolute top-6 right-6 z-10">
      <div className="w-12 h-12 rounded-xl bg-white/20 dark:bg-white/10 backdrop-blur-sm flex items-center justify-center">
        <span className="text-2xl">{status.icon}</span>
      </div>
    </div>
    <div className="relative z-10 flex-grow flex flex-col justify-center text-center">
      <div className="mb-4">
        <div
          className={`
            text-6xl font-black leading-none mb-2
            bg-gradient-to-r ${status.gradient} bg-clip-text text-transparent
            transition-all duration-300
          `}
        >
          {currentStreak}
        </div>
        <div className="text-lg font-semibold text-content-primary">Day Streak</div>
      </div>
      <div className="space-y-2">
        <p className="text-base font-medium text-content-primary">{status.message}</p>
        <p className="text-sm text-content-secondary opacity-80">{status.subMessage}</p>
        <div className="flex flex-col items-center gap-1.5 pt-1">
          <ShieldsIndicator shields={shields} />
          <ProtectedDaysBadge protectedDaysUsed={protectedDaysUsed} />
        </div>
      </div>
    </div>
    <div className="relative z-10 mt-4">
      <div className="flex justify-center space-x-1">
        {Array.from({ length: Math.min(currentStreak, 7) }, (_, i) => (
          <div
            key={i}
            className={`
              w-2 h-2 rounded-full
              bg-gradient-to-r ${status.gradient}
              transition-all duration-300
              ${i < currentStreak ? 'opacity-100 scale-100' : 'opacity-30 scale-75'}
            `}
            style={{
              animationDelay: `${i * 100}ms`,
            }}
          />
        ))}
      </div>
    </div>
    <RatioSplit sentencesRatio={sentencesRatio} wordsRatio={wordsRatio} className="relative z-10 mt-4" />
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
  </div>
);

// UPDATED: Compact view now uses the same glassmorphic style as the desktop view
const CompactStreakView = ({ currentStreak, protectedDaysUsed, shields, status, sentencesRatio, wordsRatio }) => (
    <div className="relative h-full flex flex-col p-4 rounded-2xl border-2 border-border backdrop-blur-sm overflow-hidden bg-background-secondary">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50 dark:from-white/5" />
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary rounded-full -translate-y-12 translate-x-12" />
        </div>
        <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center">
                <div className={`text-5xl font-black bg-gradient-to-r ${status.gradient} bg-clip-text text-transparent`}>
                    {currentStreak}
                </div>
                <div className="ml-3">
                    <div className="font-semibold leading-tight text-content-primary">Day</div>
                    <div className="font-semibold leading-tight text-content-primary">Streak</div>
                    <ShieldsIndicator shields={shields} className="mt-1" />
                    <ProtectedDaysBadge protectedDaysUsed={protectedDaysUsed} className="mt-1" />
                </div>
            </div>
            <span className="text-4xl">{status.icon}</span>
        </div>
        <RatioSplit sentencesRatio={sentencesRatio} wordsRatio={wordsRatio} className="relative z-10 mt-3" />
    </div>
);

const StreakCard = ({ currentStreak, protectedDaysUsed = 0, shields = 0, sentencesRatio = null, wordsRatio = null }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const getStreakStatus = () => {
    if (currentStreak === 0) {
      return {
        message: 'Start your streak today!',
        subMessage: 'Learn something new to begin',
        icon: '🎯',
        gradient: 'from-gray-400 to-gray-500 dark:from-neutral-400 dark:to-neutral-500',
      };
    }
    if (currentStreak === 1) {
      return {
        message: 'Great start!',
        subMessage: 'Keep the momentum going',
        icon: '🔥',
        gradient: 'from-orange-400 to-red-500 dark:from-orange-500 dark:to-red-600',
      };
    }
    if (currentStreak < 7) {
      return {
        message: 'Building momentum!',
        subMessage: "You're on a roll",
        icon: '⚡',
        gradient: 'from-yellow-400 to-orange-500 dark:from-yellow-500 dark:to-orange-600',
      };
    }
    if (currentStreak < 30) {
      return {
        message: 'Impressive streak!',
        subMessage: 'Consistency is key',
        icon: '🎖️',
        gradient: 'from-green-400 to-emerald-500 dark:from-green-500 dark:to-emerald-600',
      };
    }
    if (currentStreak < 60) {
      return {
        message: 'One month down!',
        subMessage: 'You are a regular learner',
        icon: '📅',
        gradient: 'from-sky-400 to-blue-500 dark:from-sky-500 dark:to-blue-600',
      };
    }
    if (currentStreak < 90) {
      return {
        message: 'Two months strong!',
        subMessage: 'Making learning a habit',
        icon: '💪',
        gradient: 'from-indigo-400 to-purple-500 dark:from-indigo-500 dark:to-purple-600',
      };
    }
    if (currentStreak < 180) {
      return {
        message: 'Three months strong!',
        subMessage: 'Truly dedicated to your craft',
        icon: '🌟',
        gradient: 'from-rose-400 to-pink-500 dark:from-rose-500 dark:to-pink-600',
      };
    }
    if (currentStreak < 365) {
      return {
        message: 'Half a year!',
        subMessage: 'Incredible dedication!',
        icon: '🏆',
        gradient: 'from-amber-400 to-yellow-500 dark:from-amber-500 dark:to-yellow-600',
      };
    }
    return {
      message: 'One year streak!',
      subMessage: 'You are unstoppable!',
      icon: '👑',
      gradient: 'from-purple-400 to-blue-500 dark:from-purple-500 dark:to-blue-600',
    };
  };

  const status = getStreakStatus();

  return isMobile ? (
    <CompactStreakView currentStreak={currentStreak} protectedDaysUsed={protectedDaysUsed} shields={shields} status={status} sentencesRatio={sentencesRatio} wordsRatio={wordsRatio} />
  ) : (
    <DesktopStreakView currentStreak={currentStreak} protectedDaysUsed={protectedDaysUsed} shields={shields} status={status} sentencesRatio={sentencesRatio} wordsRatio={wordsRatio} />
  );
};

export default StreakCard;